<template>
  <ion-page>
    <ion-content
      :fullscreen="true"
      class="h-[100svh] w-screen text-center text-grey-100 font-aktiv-grotesk"
    >
      <p
        v-if="!error"
        class="w-full h-full px-[24px] flex justify-center items-center m-auto text-[32px] leading-[40px] font-aktiv-grotesk-medium"
      >
        🔒 Logging you in...
      </p>
      <div
        v-if="error"
        class="w-full h-full px-[24px] flex flex-col justify-center items-center m-auto"
      >
        <p class="text-[32px] leading-[40px]">Something went wrong</p>
        <p class="text-[20px] leading-[28px] font-aktiv-grotesk mt-[16px]">
          We couldn't complete your login.
        </p>
        <button
          class="bg-[#00A9E0] text-white px-[40px] py-[16px] text-[20px] leading-[24px] rounded-[40px] font-aktiv-grotesk-medium mt-[24px]"
          @click="goToHome()"
        >
          Try again
        </button>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { IonContent, IonPage } from '@ionic/vue';
import { defineComponent } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import { useRoute, useRouter } from 'vue-router';
import { datadogLogs } from '@datadog/browser-logs';
import { getLoginPathObj, clearLoginPathObj } from '@/utils/redirect-uri';

export default defineComponent({
  name: 'CallbackPage',
  components: {
    IonContent,
    IonPage,
  },

  setup() {
    const { isAuthenticated, user, handleRedirectCallback } = useAuth0();
    const route = useRoute();
    const router = useRouter();

    return {
      isAuthenticated,
      user,
      route,
      router,
      handleRedirectCallback,
    };
  },
  data() {
    return {
      error: false,
    };
  },
  async mounted() {
    console.log('Callback is Mounted');

    const maxRetries = 3;
    const baseDelay = 5000;

    const attemptCallback = async (attempt = 1) => {
      try {
        console.log(`Callback attempt ${attempt}/${maxRetries}`);

        // handleRedirectCallback resolves to RedirectLoginResult ({ appState }),
        // not appState itself — same unwrap as App.vue's native handler.
        const result = await this.handleRedirectCallback();
        const appState = result && result.appState;
        if (appState && appState.fullPathObj) {
          this.router.push({
            path: appState.fullPathObj.path,
            query: appState.fullPathObj.query,
            hash: appState.fullPathObj.hash,
          });
        } else {
          this.forwardAfterLogin();
        }
        return true;
      } catch (err) {
        const userId = this.user ? this.user.sub : '';
        console.log(`Attempt ${attempt} failed:`, err.name, err.message);
        if (process.env.VUE_APP_DATADOG_CLIENT_TOKEN) {
          const message = `User (auth0Id: ${userId}) login attempt ${attempt} failed:`;
          const data = {
            auth0Id: userId,
            logType: 'login',
            errMessage: err.message,
          };
          datadogLogs.logger.warn(message, data);
        }

        if (err.message === 'Invalid state') {
          console.log('Invalid state error - forwarding after login');
          this.forwardAfterLogin();
          return true;
        }

        if (attempt >= maxRetries) {
          if (process.env.VUE_APP_DATADOG_CLIENT_TOKEN) {
            const message = `User (auth0Id: ${userId}): Max retries reached for handleRedirectCallback`;
            const data = { auth0Id: userId, logType: 'login' };
            datadogLogs.logger.error(message, data);
          }
          throw err;
        }

        const delay = baseDelay * Math.pow(2, attempt - 1);
        console.log(`Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));

        return attemptCallback(attempt + 1);
      }
    };

    let navigated = false;
    try {
      navigated = await attemptCallback();
    } catch (finalErr) {
      console.error('All callback attempts failed:', finalErr);
      this.error = true;
    }

    // Handle if user refreshes while stuck on callback page.
    // Gated on !navigated: attemptCallback already forwards on every path it
    // returns true from, and forwardAfterLogin is one-shot (it consumes
    // loginPathObj). Running it twice found nothing stored the second time and
    // pushed /home, cancelling the in-flight push to the user's real
    // destination — every web login landed on /home.
    if (!navigated && this.isAuthenticated) {
      console.log('User already authenticated, forwarding after login');
      this.forwardAfterLogin();
    }

    // Handle access denied
    if (this.route.query.error === 'access_denied') {
      console.warn('Access denied error');
      const errorDescription = this.route.query.error_description;
      if (errorDescription === 'access denied') {
        this.router.push('/access-denied');
      }
    }
  },
  methods: {
    forwardAfterLogin() {
      const loginPathObj = getLoginPathObj();
      // One-shot: consumed by this callback. A stale entry would replay the
      // stored deep link (and re-count its src attribution) on a later
      // same-tab re-login — the web analogue of the native deepLinkPathObj
      // replay fixed in App.vue.
      clearLoginPathObj();
      if (loginPathObj && loginPathObj.path !== '/') {
        console.log('forwarding login path:', JSON.stringify(loginPathObj));
        this.$router.push({
          path: loginPathObj.path,
          query: loginPathObj.query,
          hash: loginPathObj.hash,
        });
      } else {
        this.router.push('/home');
      }
    },
    goToHome() {
      this.$router.push('/home');
    },
  },
});
</script>
